import React from 'react';
import {Box, Grid, Typography, Button, Tooltip} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {
  PersonAdd as PersonAddIcon,
  LocalShipping as LocalShippingIcon,
  Search as SearchIcon,
  OndemandVideo as VideoIcon,
} from '@mui/icons-material';
import {EntioCard} from 'src/components/ui';
import {brand, semantic, neutral} from 'src/theme';

interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  onClick: () => void;
  allowedRoles?: string[];
}

interface QuickActionsCardProps {
  onAddResident?: () => void;
  onAddDelivery?: () => void;
  disableAddResident?: boolean;
}

export default function QuickActionsCard({
  onAddResident,
  onAddDelivery,
  disableAddResident,
}: QuickActionsCardProps) {
  const navigate = useNavigate();
  const account = useAppSelector(selectAccount);
  const {isAtLimit} = useLicenseQuota();

  const allQuickActions: QuickAction[] = [
    {
      title: 'Add Resident',
      description: 'Register new resident',
      icon: <PersonAddIcon />,
      color: brand[500],
      bgColor: brand[100],
      allowedRoles: ['Org Admin', 'Org Owner'],
      onClick: () => {
        if (onAddResident) {
          onAddResident();
        } else {
          navigate('/tenant');
        }
      },
    },
    {
      title: 'Record Delivery',
      description: 'Log new delivery',
      icon: <LocalShippingIcon />,
      color: neutral[600],
      bgColor: neutral[100],
      allowedRoles: ['Org Admin', 'Org Owner'],
      onClick: () => {
        if (onAddDelivery) {
          onAddDelivery();
        } else {
          navigate('/delivery');
        }
      },
    },
    {
      title: 'Search Events',
      description: 'Find call & door events',
      icon: <SearchIcon />,
      color: semantic.info.main,
      bgColor: semantic.info.light,
      allowedRoles: ['Org Operator', 'Org Admin', 'Org Owner', 'Org Manager'],
      onClick: () => {
        navigate('/eventSearch');
      },
    },
    {
      title: 'Tutorial Videos',
      description: 'Learn how to use Entrio',
      icon: <VideoIcon />,
      color: semantic.error.main,
      bgColor: semantic.error.light,
      allowedRoles: ['Org Operator', 'Org Admin', 'Org Owner', 'Org Manager'],
      onClick: () => {
        window.open(
          'https://www.youtube.com/playlist?list=PLz5V7TwC7QwtxcDvq0jZERQTAN42PfkFv',
          '_blank'
        );
      },
    },
  ];

  const quickActions = allQuickActions.filter(action => {
    if (!action.allowedRoles) return true;
    return action.allowedRoles.includes(account.role?.name || '');
  });

  const isActionDisabled = (actionTitle: string) => {
    if (actionTitle === 'Add Resident' && disableAddResident) return true;
    if (!isAtLimit) return false;
    return (
      actionTitle === 'Add Resident' ||
      actionTitle === 'Search Events' ||
      actionTitle === 'Record Delivery'
    );
  };

  const getDisabledTooltip = (actionTitle: string) => {
    if (actionTitle === 'Add Resident') {
      if (disableAddResident) {
        return 'Residents are managed from PDK while auto-sync is on.';
      }
      return 'License limit exceeded. Add more licenses to manage residents.';
    }
    if (actionTitle === 'Search Events') {
      return 'License limit exceeded. Add more licenses to search events.';
    }
    if (actionTitle === 'Record Delivery') {
      return 'License limit exceeded. Add more licenses to record deliveries.';
    }
    return '';
  };

  return (
    <EntioCard title="Quick Actions">
      <Grid container spacing={1.5}>
        {quickActions.map((action, index) => {
          const disabled = isActionDisabled(action.title);
          const button = (
            <Button
              onClick={action.onClick}
              disabled={disabled}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: '80px',
                p: 1.5,
                border: `1px solid ${neutral[200]}`,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                bgcolor: disabled ? neutral[50] : 'white',
                opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: disabled ? neutral[50] : action.bgColor,
                  borderColor: disabled ? neutral[200] : action.color,
                  transform: disabled ? 'none' : 'translateY(-2px)',
                  boxShadow: disabled ? 'none' : '0 4px 8px rgba(0,0,0,0.1)',
                },
                '&.Mui-disabled': {
                  bgcolor: neutral[50],
                  opacity: 0.6,
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: action.bgColor,
                  color: action.color,
                  mb: 1,
                  '& svg': {
                    fontSize: 18,
                  },
                }}
              >
                {action.icon}
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: neutral[800],
                  mb: 0.25,
                }}
              >
                {action.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 11,
                  color: neutral[500],
                  lineHeight: 1.3,
                }}
              >
                {action.description}
              </Typography>
            </Button>
          );

          return (
            <Grid item xs={6} key={index}>
              {disabled ? (
                <Tooltip title={getDisabledTooltip(action.title)} arrow placement="top">
                  <span style={{display: 'block', width: '100%'}}>{button}</span>
                </Tooltip>
              ) : (
                button
              )}
            </Grid>
          );
        })}
      </Grid>
    </EntioCard>
  );
}
